import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFlag, useFlags, useFlagsStatus } from '@unleash/proxy-client-react';

import {
  fetchDealerForPromoBanner,
  fetchPromoBannerTimeConfig,
  fetchPurchasesForPromoEligible,
} from '../services/promoNovosLojistas';
import { getUnleashEnvironment, UNLEASH_APP_NAME } from '../constants/unleashConfig';
import {
  computePromoBannerCountdown,
  DEFAULT_BADGE,
  formatCouponBonusAmount,
  formatDurationHoursForDisplay,
  computePromoEligible,
  getPromoBannerVisibilityChecks,
  isPromoUnleashEnabled,
  shouldFetchPurchasesForPromo,
  parseDurationMsFromMinutes,
  parseTimeBenefitsBannerResponse,
  resolvePromoBannerStartAt,
  UNLEASH_PROMO_FLAG,
} from '../utils/promoNovosLojistas';

const buildBannerConfig = ({
  countdown,
  durationHoursLabel,
  bonusAmount,
}) => ({
  endTimestamp: countdown.endTimestamp,
  config: {
    visible: true,
    link: '',
    isClickable: false,
    bonusAmount,
    badgeText: DEFAULT_BADGE,
    durationHoursLabel,
    endTimestamp: countdown.endTimestamp,
    startTimestamp: countdown.startTimestamp,
    totalDurationMs: countdown.totalDurationMs,
  },
});

export const usePromoNovosLojistasBanner = () => {
  const authUser = useSelector(state => state.authentication?.user);
  const isLoggedIn = authUser?.isLoggedIn?.() ?? false;

  const promoFlagFromHook = useFlag(UNLEASH_PROMO_FLAG);
  const enabledFlags = useFlags();
  const { flagsReady } = useFlagsStatus();

  const promoFlagFromList = useMemo(
    () => enabledFlags.some(flag => flag.name === UNLEASH_PROMO_FLAG),
    [enabledFlags]
  );

  const promoFlagEnabled = promoFlagFromHook || promoFlagFromList;

  const [dealer, setDealer] = useState(null);
  const [dealerLoading, setDealerLoading] = useState(true);
  const [dealerError, setDealerError] = useState(null);

  const [timeConfig, setTimeConfig] = useState(null);
  const [timeConfigLoading, setTimeConfigLoading] = useState(true);

  const [purchases, setPurchases] = useState([]);
  const [purchasesResolved, setPurchasesResolved] = useState(false);
  const [purchasesCheckFailed, setPurchasesCheckFailed] = useState(false);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [purchasesError, setPurchasesError] = useState(null);

  const dealerFetchIdRef = useRef(0);
  const timeConfigFetchIdRef = useRef(0);
  const purchasesFetchIdRef = useRef(0);

  const loadDealer = useCallback(async () => {
    const fetchId = ++dealerFetchIdRef.current;

    if (!isLoggedIn) {
      setDealer(null);
      setDealerError(null);
      setDealerLoading(false);
      return;
    }

    setDealerLoading(true);
    setDealerError(null);

    try {
      const data = await fetchDealerForPromoBanner();
      if (fetchId !== dealerFetchIdRef.current) {
        return;
      }
      setDealer(data);
    } catch (error) {
      if (fetchId !== dealerFetchIdRef.current) {
        return;
      }
      console.warn('Erro ao carregar dealer para banner promo:', error);
      setDealer(null);
      setDealerError(error?.message ?? 'dealer_fetch_failed');
    } finally {
      if (fetchId === dealerFetchIdRef.current) {
        setDealerLoading(false);
      }
    }
  }, [isLoggedIn]);

  const loadTimeConfig = useCallback(async () => {
    const fetchId = ++timeConfigFetchIdRef.current;
    setTimeConfigLoading(true);

    try {
      const data = await fetchPromoBannerTimeConfig();
      if (fetchId !== timeConfigFetchIdRef.current) {
        return;
      }
      setTimeConfig(data);
    } catch (error) {
      if (fetchId !== timeConfigFetchIdRef.current) {
        return;
      }
      console.warn('Erro ao carregar config de tempo do banner promo:', error);
      setTimeConfig(parseTimeBenefitsBannerResponse({}));
    } finally {
      if (fetchId === timeConfigFetchIdRef.current) {
        setTimeConfigLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadDealer();
    return () => {
      dealerFetchIdRef.current += 1;
    };
  }, [loadDealer]);

  useEffect(() => {
    loadTimeConfig();
    return () => {
      timeConfigFetchIdRef.current += 1;
    };
  }, [loadTimeConfig]);

  const loadPurchasesEligibility = useCallback(async () => {
    const fetchId = ++purchasesFetchIdRef.current;

    if (!isLoggedIn) {
      setPurchases([]);
      setPurchasesResolved(true);
      setPurchasesCheckFailed(false);
      setPurchasesError(null);
      setPurchasesLoading(false);
      return;
    }

    if (dealerLoading || timeConfigLoading) {
      return;
    }

    const needsPurchasesCheck = shouldFetchPurchasesForPromo({ dealer, timeConfig });

    if (!needsPurchasesCheck) {
      if (fetchId !== purchasesFetchIdRef.current) {
        return;
      }
      setPurchases([]);
      setPurchasesResolved(true);
      setPurchasesCheckFailed(false);
      setPurchasesError(null);
      setPurchasesLoading(false);
      return;
    }

    if (fetchId === purchasesFetchIdRef.current) {
      setPurchasesLoading(true);
      setPurchasesResolved(false);
      setPurchasesCheckFailed(false);
      setPurchasesError(null);
    }

    try {
      const list = await fetchPurchasesForPromoEligible({ dealer, timeConfig });
      if (fetchId !== purchasesFetchIdRef.current) {
        return;
      }
      setPurchases(list);
    } catch (error) {
      if (fetchId !== purchasesFetchIdRef.current) {
        return;
      }
      console.warn('Erro ao verificar disputas ganhas para banner promo:', error);
      setPurchases([]);
      setPurchasesCheckFailed(true);
      setPurchasesError(error?.message ?? 'purchases_fetch_failed');
    } finally {
      if (fetchId === purchasesFetchIdRef.current) {
        setPurchasesResolved(true);
        setPurchasesLoading(false);
      }
    }
  }, [isLoggedIn, dealer, dealerLoading, timeConfig, timeConfigLoading]);

  useEffect(() => {
    loadPurchasesEligibility();
    return () => {
      purchasesFetchIdRef.current += 1;
    };
  }, [loadPurchasesEligibility]);

  const durationMinutes = timeConfig?.homeCouponBannerTimer;

  const durationMs = useMemo(
    () => parseDurationMsFromMinutes(durationMinutes),
    [durationMinutes]
  );

  const durationHoursLabel = useMemo(
    () => formatDurationHoursForDisplay(durationMinutes),
    [durationMinutes]
  );

  const bonusAmount = useMemo(
    () => formatCouponBonusAmount(timeConfig?.couponValue),
    [timeConfig?.couponValue]
  );

  const promoStartAt = resolvePromoBannerStartAt(dealer);

  const timingBounds = useMemo(
    () =>
      computePromoBannerCountdown({
        startAt: promoStartAt,
        durationMs,
      }),
    [promoStartAt, durationMs]
  );

  const { endTimestamp } = timingBounds;

  const [countdown, setCountdown] = useState(timingBounds);

  useEffect(() => {
    setCountdown(
      computePromoBannerCountdown({
        startAt: promoStartAt,
        durationMs,
      })
    );
  }, [endTimestamp, promoStartAt, durationMs]);

  useEffect(() => {
    if (!endTimestamp) {
      return undefined;
    }

    let cancelled = false;

    const tick = () => {
      if (cancelled) {
        return;
      }

      setCountdown(
        computePromoBannerCountdown({
          startAt: promoStartAt,
          durationMs,
          now: Date.now(),
        })
      );
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [endTimestamp, promoStartAt, durationMs]);

  const state = useMemo(
    () =>
      buildBannerConfig({
        countdown,
        durationHoursLabel,
        bonusAmount,
      }),
    [countdown, durationHoursLabel, bonusAmount]
  );

  const needsPurchasesCheck = useMemo(
    () => shouldFetchPurchasesForPromo({ dealer, timeConfig }),
    [dealer, timeConfig]
  );

  const dataLoading =
    timeConfigLoading ||
    dealerLoading ||
    (needsPurchasesCheck && (purchasesLoading || !purchasesResolved));

  const promoEligible = useMemo(
    () =>
      computePromoEligible({
        dealer,
        timeConfig,
        purchases,
        purchasesResolved: needsPurchasesCheck ? purchasesResolved : true,
        purchasesCheckFailed: needsPurchasesCheck ? purchasesCheckFailed : false,
        skipPurchasesCheck: !needsPurchasesCheck,
      }),
    [
      dealer,
      timeConfig,
      purchases,
      purchasesResolved,
      purchasesCheckFailed,
      needsPurchasesCheck,
    ]
  );

  const promoFeatureEnabled = isPromoUnleashEnabled(promoFlagEnabled);

  const showPromo =
    !dataLoading &&
    flagsReady &&
    isLoggedIn &&
    promoFeatureEnabled &&
    promoEligible &&
    countdown.isActive &&
    state.config.visible;

  const lastDiagnosticKeyRef = useRef('');

  useEffect(() => {
    if (dataLoading || showPromo) {
      if (showPromo) {
        lastDiagnosticKeyRef.current = '';
      }
      return;
    }

    const checks = getPromoBannerVisibilityChecks({
      configLoading: dataLoading,
      dealerLoading,
      purchasesLoading,
      isLoggedIn,
      promoFlagEnabled,
      promoFeatureEnabled,
      flagsReady,
      dealer,
      dealerError,
      purchasesError,
      purchases,
      purchasesResolved,
      purchasesCheckFailed,
      timeConfig,
      countdown,
    });

    const diagnosticKey = JSON.stringify({
      unleashApp: UNLEASH_APP_NAME,
      unleashEnvironment: getUnleashEnvironment(),
      promoFlagFromHook,
      promoFlagFromList,
      ...checks,
    });

    if (lastDiagnosticKeyRef.current === diagnosticKey) {
      return;
    }

    lastDiagnosticKeyRef.current = diagnosticKey;
    console.warn('[PromoBanner] Exibindo IPVA — diagnóstico:', {
      ...checks,
      unleashApp: UNLEASH_APP_NAME,
      unleashEnvironment: getUnleashEnvironment(),
      promoFlagFromHook,
      promoFlagFromList,
      flagName: UNLEASH_PROMO_FLAG,
    });
  }, [
    dataLoading,
    showPromo,
    isLoggedIn,
    promoFlagEnabled,
    promoFlagFromHook,
    promoFlagFromList,
    promoFeatureEnabled,
    flagsReady,
    promoEligible,
    countdown.isActive,
    dealer,
    dealerError,
    purchasesError,
    purchases,
    purchasesResolved,
    purchasesCheckFailed,
    timeConfig,
    dealerLoading,
    purchasesLoading,
  ]);

  return {
    showPromo,
    loading: dataLoading || !flagsReady,
    bannerConfig: state.config,
    remainingMs: countdown.remainingMs,
    remainingSeconds: countdown.remainingMs / 1000,
    progress: countdown.progress,
    refetch: () => {
      loadDealer();
      loadTimeConfig();
      loadPurchasesEligibility();
    },
  };
};
